import { observer } from 'mobx-react-lite'
import { Command, EditorState } from 'prosemirror-state'

import { useMst } from '#state'

import { ToolbarButton, type ToolbarButtonProps } from './ToolbarButton'

interface ToolbarEditorButtonProps extends Omit<ToolbarButtonProps, 'active'> {
  /**
   * The button will invoke this Prosemirror command on click, and show a
   * disabled state if the command doesn't apply, the `disabled` prop is set, or
   * the script's plan restricts editing
   */
  command: Command
  /**
   * Active state for the button. If a function, will be called with the editor
   * state for the caller to determine whether it is active or not
   */
  active?: boolean | ((editorState: EditorState) => boolean)
}

export const ToolbarEditorButton = observer<ToolbarEditorButtonProps>(
  (props) => {
    'use memo'

    const { command, onClick, ...buttonProps } = props
    let disabled = props.disabled
    let active

    const { currentScript } = useMst()
    const editorState = currentScript?.pmEditor.editorState

    if (editorState && command) {
      disabled = disabled || !command(editorState)
    }
    if (currentScript?.readOnlyByPlan) {
      disabled = true
    }

    if (typeof props.active === 'function') {
      if (editorState) active = props.active(editorState)
    } else {
      active = props.active
    }

    function handleClick() {
      currentScript?.pmEditor.invoke(command)
      onClick?.()
    }

    return (
      <ToolbarButton
        {...buttonProps}
        onClick={handleClick}
        disabled={disabled}
        active={active}
      />
    )
  },
)

ToolbarEditorButton.displayName = 'Toolbar.EditorButton'
